import { memo, useMemo } from 'react';
import { PEERS, boxOf, cellLabel, colOf, rowOf } from '../game/geometry.ts';
import { cellNotes, type GameState } from '../game/state.ts';

interface BoardProps {
  game: GameState;
  /** Cells whose digit repeats in their row, column or box. */
  clashCells: Set<number>;
  onSelect: (cell: number) => void;
}

const NOTE_DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const Board = memo(function Board({ game, clashCells, onSelect }: BoardProps) {
  const { values, given, selected, wrong, hint, status } = game;
  const hidden = status === 'paused';

  const marks = useMemo(() => {
    const sel = selected ?? -1;
    const selDigit = sel >= 0 ? values[sel] : 0;
    const wrongSet = new Set(wrong);
    const focus = new Set<number>();
    const pattern = new Set<number>();
    const targets = new Set<number>();
    const elims = new Map<number, Set<number>>();
    if (hint?.kind === 'step') {
      if (hint.stage === 1) hint.step.focusCells.forEach((c) => focus.add(c));
      else {
        hint.step.reveal.cells.forEach((c) => pattern.add(c));
        hint.step.reveal.placements.forEach((p) => targets.add(p.cell));
        for (const e of hint.step.reveal.eliminations) {
          if (!elims.has(e.cell)) elims.set(e.cell, new Set());
          elims.get(e.cell)!.add(e.digit);
        }
      }
    }
    return { sel, selDigit, wrongSet, focus, pattern, targets, elims };
  }, [selected, values, wrong, hint]);

  return (
    <div className={`board${hidden ? ' is-hidden' : ''}`} role="grid" aria-label="Sudoku board" aria-hidden={hidden}>
      {Array.from({ length: 9 }, (_, box) => (
        <div className="box" key={box} role="presentation">
          {Array.from({ length: 9 }, (_, i) => {
            const cell = (3 * Math.floor(box / 3) + Math.floor(i / 3)) * 9 + 3 * (box % 3) + (i % 3);
            const v = values[cell];
            const isSel = cell === marks.sel;
            const related =
              marks.sel >= 0 &&
              !isSel &&
              (rowOf(cell) === rowOf(marks.sel) || colOf(cell) === colOf(marks.sel) || boxOf(cell) === boxOf(marks.sel));
            const cls = [
              'cell',
              given[cell] ? 'is-given' : v ? 'is-player' : '',
              isSel ? 'is-selected' : '',
              related ? 'is-related' : '',
              v && v === marks.selDigit && !isSel ? 'is-same' : '',
              marks.wrongSet.has(cell) ? 'is-wrong' : '',
              clashCells.has(cell) ? 'is-clash' : '',
              marks.focus.has(cell) ? 'is-focus' : '',
              marks.pattern.has(cell) ? 'is-pattern' : '',
              marks.targets.has(cell) ? 'is-target' : '',
            ]
              .filter(Boolean)
              .join(' ');
            const { shown: n, struck } = cellNotes(game, cell);
            const noteList = NOTE_DIGITS.filter((d) => n & (1 << d) && !(struck & (1 << d)));
            // A manual note is stale when that digit is already placed in the cell's row, column or box.
            const stale =
              v || game.notesKind === 'auto'
                ? 0
                : PEERS[cell].reduce((m, p) => (values[p] ? m | (1 << values[p]) : m), 0) & n;
            const label = `${cellLabel(cell)}, ${
              v
                ? `${v}${given[cell] ? ', given' : ''}${clashCells.has(cell) ? ', repeated' : ''}${marks.wrongSet.has(cell) ? ', wrong' : ''}`
                : noteList.length
                  ? `notes ${noteList.join(' ')}`
                  : 'empty'
            }`;
            const cellElims = marks.elims.get(cell);
            return (
              <button
                type="button"
                key={cell}
                role="gridcell"
                className={cls}
                aria-label={label}
                aria-selected={isSel}
                tabIndex={isSel ? 0 : -1}
                data-cell={cell}
                onClick={() => onSelect(cell)}
                disabled={hidden}
              >
                {v ? (
                  <span className="digit">{v}</span>
                ) : n || cellElims ? (
                  <span className="notes" aria-hidden="true">
                    {NOTE_DIGITS.map((d) => (
                      <span
                        key={d}
                        className={`note${cellElims?.has(d) ? ' is-elim' : ''}${stale & (1 << d) ? ' is-stale' : ''}${struck & (1 << d) ? ' is-struck' : ''}${d === marks.selDigit ? ' is-same' : ''}`}
                      >
                        {n & (1 << d) || cellElims?.has(d) ? d : ''}
                      </span>
                    ))}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
});
